import { useEffect, useState } from "react";
import { useRecordContext } from "react-admin";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { callApi } from "@/dataprovider/misc-apis";

type Demographics = {
  totalRsvpAttendees: number;
  male: number;
  female: number;
  childrenUnder10: number;
};

type HistoryGroup = {
  occurrenceDate: string;
  demographics: Demographics;
};

type RsvpHistoryResponse = {
  miqaatId: string;
  groups: HistoryGroup[];
};

type Props = {
  embedded?: boolean;
};

function OccurrenceStats({ s }: { s: Demographics }) {
  const cells = [
    { v: s.male, l: "Male" },
    { v: s.female, l: "Female" },
    { v: s.childrenUnder10, l: "Children (<10)" },
    { v: s.totalRsvpAttendees, l: "Total" },
  ];
  return (
    <Grid container spacing={2}>
      {cells.map((c) => (
        <Grid key={c.l} size={{ xs: 6, sm: 3 }}>
          <Typography variant="h6" fontWeight={600} component="p" sx={{ lineHeight: 1.2 }}>
            {c.v}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            {c.l}
          </Typography>
        </Grid>
      ))}
    </Grid>
  );
}

export default function MiqaatRsvpHistory({ embedded = false }: Props) {
  const record = useRecordContext();
  const [data, setData] = useState<RsvpHistoryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const id = record?.id;
    const dateMode = record?.dateMode;
    if (!id || dateMode !== "PERMANENT") return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void callApi({
      location: "miqaats",
      method: "GET",
      id: `${String(id)}/rsvp-history`,
    })
      .then((res) => {
        if (!cancelled) {
          setData((res.data ?? null) as RsvpHistoryResponse | null);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          const msg =
            e && typeof e === "object" && "message" in e
              ? String((e as { message?: string }).message)
              : "Failed to load RSVP history";
          setError(msg);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [record?.id, record?.dateMode]);

  if (!record?.id || record?.dateMode !== "PERMANENT") {
    return null;
  }

  const content = (
    <>
      {!embedded && (
        <Typography
          variant="overline"
          color="text.secondary"
          display="block"
          sx={{ letterSpacing: 0.5 }}
        >
          Past occurrences
        </Typography>
      )}
      {!embedded && (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mt: 1, mb: 1.5 }}
        >
          Demographics by stored Gregorian date (one row per Hijri year when families RSVP).
        </Typography>
      )}
      {loading && <CircularProgress size={22} sx={{ my: 1 }} />}
      {error && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}
      {!loading && !error && data && data.groups.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No historical RSVPs yet.
        </Typography>
      )}
      {!loading && !error && data && data.groups.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            mt: 0.5,
          }}
        >
          {data.groups.map((g, idx) => {
            const s = g.demographics;
            const subtitle =
              s.totalRsvpAttendees > 0 ? `${s.totalRsvpAttendees} RSVP’d` : "No attendees";
            return (
              <Accordion
                key={g.occurrenceDate}
                defaultExpanded={idx === 0}
                elevation={0}
                disableGutters
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  "&:before": { display: "none" },
                  "&.Mui-expanded": { my: 0.5 },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {g.occurrenceDate}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {subtitle}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                  <OccurrenceStats s={s} />
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}
    </>
  );

  if (embedded) {
    return <Box>{content}</Box>;
  }

  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent sx={{ "&:last-child": { pb: 2 } }}>{content}</CardContent>
    </Card>
  );
}
